// Thin owner wrapper: calls the zone's F-F React Query mutation hooks and threads
// them (id-bound) into the shared <MediaPanelContainer> (review #8). Rendered by
// ZoneForm in edit mode only. Snake_case wire fields per ADR-0020.

import type { FC } from "react";

import type { Zone, ZoneUpdate } from "@shared/api/zones";
import {
  useGenerateZoneNarration,
  useRemoveZoneMedia,
  useSetZoneNarration,
  useUpdateZone,
  useUpdateZoneMedia,
  useUploadZoneMedia,
  useUploadZoneNarration,
} from "@shared/api/hooks/useZones";
import type { AudioLanguage } from "@shared/hooks/useTextToSpeech";

import MediaPanelContainer from "@staff/features/media/MediaPanelContainer";

// Legacy voice-URL field key by language (camelCase per ADR-0020).
const VOICE_URL_KEY: Record<AudioLanguage, keyof ZoneUpdate> = {
  tc: "voiceUrlTc",
  en: "voiceUrlEn",
  sc: "voiceUrlSc",
};

export interface ZoneMediaPanelProps {
  zone: Zone;
  disabled?: boolean;
  unsavedChanges?: boolean;
  descriptionChanged?: Partial<Record<AudioLanguage, boolean>>;
}

const ZoneMediaPanel: FC<ZoneMediaPanelProps> = ({
  zone,
  disabled,
  unsavedChanges,
  descriptionChanged,
}) => {
  const generate = useGenerateZoneNarration();
  const uploadNarration = useUploadZoneNarration();
  const setNarration = useSetZoneNarration();
  const updateZone = useUpdateZone();
  const uploadMedia = useUploadZoneMedia();
  const updateMedia = useUpdateZoneMedia();
  const removeMedia = useRemoveZoneMedia();

  const zoneId = zone.id;

  return (
    <MediaPanelContainer
      ownerKey="zone"
      entity={zone}
      disabled={disabled}
      unsavedChanges={unsavedChanges}
      descriptionChanged={descriptionChanged}
      generatingLang={
        generate.isPending
          ? (generate.variables?.language as AudioLanguage)
          : null
      }
      uploadingMedia={uploadMedia.isPending}
      ops={{
        generate: (language) => generate.mutateAsync({ zoneId, language }),
        uploadNarration: (language, file) =>
          uploadNarration.mutateAsync({ zoneId, language, file }),
        setNarrationNull: (language) =>
          setNarration.mutateAsync({
            zoneId,
            language,
            payload: { assetId: null },
          }),
        clearVoiceUrl: (language) =>
          updateZone.mutateAsync({
            zoneId,
            payload: { [VOICE_URL_KEY[language]]: "" } as ZoneUpdate,
          }),
        saveVoiceUrl: (language, url) =>
          updateZone.mutateAsync({
            zoneId,
            payload: { [VOICE_URL_KEY[language]]: url } as ZoneUpdate,
          }),
        uploadMedia: ({ file, kind, language, caption }) =>
          uploadMedia.mutateAsync({ zoneId, file, kind, language, caption }),
        updateMedia: (assetId, patch) =>
          updateMedia.mutateAsync({ zoneId, assetId, ...patch }),
        removeMedia: (assetId) => removeMedia.mutateAsync({ zoneId, assetId }),
      }}
    />
  );
};

export default ZoneMediaPanel;
